import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { meetingsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Calendar, Clock, Users, Globe, Lock, AlertCircle } from 'lucide-react';

const TOPICS = [
  { value: 'alcool', label: 'Álcool' },
  { value: 'nicotina', label: 'Nicotina' },
  { value: 'drogas', label: 'Drogas' },
  { value: 'emocoes', label: 'Emoções' },
  { value: 'jogos-apostas', label: 'Jogos e Apostas' },
  { value: 'geral', label: 'Geral' },
];

const DURATIONS = [
  { value: 15, label: '15 minutos' },
  { value: 30, label: '30 minutos' },
  { value: 45, label: '45 minutos' },
  { value: 60, label: '1 hora' },
  { value: 90, label: '1 hora e 30 minutos' },
  { value: 120, label: '2 horas' },
  { value: 180, label: '3 horas' },
];

export function MeetingCreate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Form state
  const [title, setTitle] = useState('');
  const [topic, setTopic] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [duration, setDuration] = useState(60);
  const [capacityMax, setCapacityMax] = useState(50);
  const [visibility, setVisibility] = useState('public');
  const [allowAnonymous, setAllowAnonymous] = useState(false);

  // Get current timezone
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const validateForm = () => {
    const newErrors = {};

    // Title validation
    if (!title.trim()) {
      newErrors.title = 'Título é obrigatório';
    } else if (title.length < 3 || title.length > 80) {
      newErrors.title = 'Título deve ter entre 3 e 80 caracteres';
    }

    // Topic validation
    if (!topic) {
      newErrors.topic = 'Tópico é obrigatório';
    }

    // Date/time validation
    if (!startDate) {
      newErrors.startDate = 'Data é obrigatória';
    }
    if (!startTime) {
      newErrors.startTime = 'Hora é obrigatória';
    }

    if (startDate && startTime) {
      const startDateTime = new Date(`${startDate}T${startTime}`);
      const now = new Date();
      const minStart = new Date(now.getTime() + 5 * 60000); // 5 minutes from now

      if (startDateTime < minStart) {
        newErrors.startDate = 'Data/hora deve ser no mínimo 5 minutos no futuro';
      }
    }

    // Capacity validation
    if (capacityMax < 2 || capacityMax > 300) {
      newErrors.capacityMax = 'Capacidade deve estar entre 2 e 300';
    }

    // Description validation (optional but has max length)
    if (description.length > 280) {
      newErrors.description = 'Descrição não pode ter mais de 280 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    setLoading(true);
    try {
      // Combine date and time into ISO string
      const startAt = new Date(`${startDate}T${startTime}`).toISOString();

      const meetingData = {
        title: title.trim(),
        topic,
        description: description.trim(),
        start_at: startAt,
        duration_min: duration,
        timezone,
        visibility,
        capacity_max: capacityMax,
        rules: [],
        requires_accept_rules: true,
      };

      const response = await meetingsAPI.createMeeting(meetingData);
      toast.success('Sala criada com sucesso!');
      
      // Redirect to meeting detail
      navigate(`/reunioes/${response.data.slug}`);
    } catch (error) {
      console.error('Failed to create meeting:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao criar sala';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const remainingChars = 280 - description.length;
  const isOverLimit = remainingChars < 0;

  return (
    <div className="max-w-2xl mx-auto py-6 px-4" data-testid="meeting-create-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Criar Sala de Reunião</h1>
        <p className="text-muted-foreground">
          Configure sua sala para compartilhar experiências e apoiar outros membros
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Informações Básicas</CardTitle>
            <CardDescription>
              Detalhes principais sobre a reunião
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Title */}
            <div>
              <Label htmlFor="title">
                Título <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Grupo de Apoio Álcool"
                maxLength={80}
                data-testid="title-input"
                aria-invalid={!!errors.title}
                aria-describedby={errors.title ? 'title-error' : undefined}
              />
              {errors.title && (
                <p id="title-error" className="text-sm text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.title}
                </p>
              )}
              <p className="text-sm text-muted-foreground mt-1">
                {title.length}/80 caracteres
              </p>
            </div>

            {/* Topic */}
            <div>
              <Label htmlFor="topic">
                Tópico <span className="text-red-500">*</span>
              </Label>
              <Select value={topic} onValueChange={setTopic}>
                <SelectTrigger id="topic" data-testid="topic-select" aria-invalid={!!errors.topic}>
                  <SelectValue placeholder="Selecione o tópico" />
                </SelectTrigger>
                <SelectContent>
                  {TOPICS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.topic && (
                <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.topic}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descreva brevemente sobre o que será a reunião..."
                className={`min-h-[80px] resize-none ${isOverLimit ? 'border-red-500' : ''}`}
                data-testid="description-textarea"
                aria-invalid={!!errors.description}
              />
              <div className="flex items-center justify-between mt-1">
                {errors.description && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.description}
                  </p>
                )}
                <div className="flex-1" />
                <p
                  className={`text-sm ${
                    isOverLimit
                      ? 'text-red-500'
                      : remainingChars < 50
                      ? 'text-yellow-600'
                      : 'text-muted-foreground'
                  }`}
                >
                  {remainingChars} caracteres restantes
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Data e Horário
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Start Date */}
              <div>
                <Label htmlFor="startDate">
                  Data <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="start-date-input"
                  aria-invalid={!!errors.startDate}
                />
                {errors.startDate && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.startDate}
                  </p>
                )}
              </div>

              {/* Start Time */}
              <div>
                <Label htmlFor="startTime">
                  Hora <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="startTime"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  data-testid="start-time-input"
                  aria-invalid={!!errors.startTime}
                />
                {errors.startTime && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.startTime}
                  </p>
                )}
              </div>
            </div>

            {/* Duration */}
            <div>
              <Label htmlFor="duration">
                Duração <span className="text-red-500">*</span>
              </Label>
              <Select value={String(duration)} onValueChange={(val) => setDuration(Number(val))}>
                <SelectTrigger id="duration" data-testid="duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATIONS.map((d) => (
                    <SelectItem key={d.value} value={String(d.value)}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Timezone info */}
            <p className="text-sm text-muted-foreground">
              <Clock className="inline h-3 w-3 mr-1" />
              Fuso horário: {timezone}
            </p>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Configurações da Sala
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Capacity */}
            <div>
              <Label htmlFor="capacity">
                Capacidade Máxima <span className="text-red-500">*</span>
              </Label>
              <Input
                id="capacity"
                type="number"
                min={2}
                max={300}
                value={capacityMax}
                onChange={(e) => setCapacityMax(Number(e.target.value))}
                data-testid="capacity-input"
                aria-invalid={!!errors.capacityMax}
              />
              {errors.capacityMax && (
                <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.capacityMax}
                </p>
              )}
              <p className="text-sm text-muted-foreground mt-1">
                Número máximo de participantes (2-300)
              </p>
            </div>

            {/* Visibility */}
            <div>
              <Label>Visibilidade</Label>
              <RadioGroup value={visibility} onValueChange={setVisibility}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="public" id="public" />
                  <Label htmlFor="public" className="flex items-center gap-2 font-normal cursor-pointer">
                    <Globe className="h-4 w-4" />
                    Pública - Qualquer um pode ver e participar
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="private" id="private" />
                  <Label htmlFor="private" className="flex items-center gap-2 font-normal cursor-pointer">
                    <Lock className="h-4 w-4" />
                    Privada - Apenas convidados podem participar
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/reunioes')}
            disabled={loading}
            data-testid="cancel-button"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading || isOverLimit}
            data-testid="submit-button"
            className="flex-1"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Criando...
              </>
            ) : (
              'Criar Sala'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
